import { DynamicFormRef } from "@mavichpe/dynamic-forms";
import { useEffect, useRef, useState } from "react";
import InvoiceTemplateFormConfig from "./form";
import styles from "./style.module.scss";
import { InvoiceTemplateFormData, InvoiceTemplateRelatedAttributes } from "@/formTypes/InvoiceTemplateDataType";
import GenericForm, { ConfirmationConfig } from "../GenericForm";
import { InvoiceTemplateApi, InvoiceTemplateDataType } from "@/api/InvoiceTemplateApi";
import QuickAddProductForm from "../QuickAddProductForm";
import { Col, Row } from "reactstrap";
import PriceSelector from "@components/dynamic/PriceSelector";
import { PriceApi } from "@/api/PricesApi";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { selectEDocumentSummary, selectEDocumentVentaNeta } from "@/store/slices/utils/eDocumentSelectors";
import { ON_INVOICE_DISCOUNT_TYPE_CODE } from "@utils/Constants";
import { clearDocument, loadProducts, setDocumentDiscount, setDocumentOtherCharges, setPrice, setPriceCode } from "@/store/slices/eDocumentSlides";
import { CurrencyApi } from "@/api/CurrencyApi";
import { setCurrency } from "@/store/slices/currencySlides";
import LoadingScreen from "@components/content/LoadingScreen";
import EdocumentProductList from "@components/dynamic/EdocumentProductList";
import { generateNextNumberByModel, omitProperties, removeFunctionFromObject } from "@utils/Utils";
import { CurrencyIdPubSub } from "@utils/pubsubs/PubSub";
import { flushDocumentProductEdits } from "@utils/eDocumentFlush";
import { InvoiceTemplateProductDataType } from "@/api/InvoiceTemplateProductApi";
import { DocumentAutoGeneratedNumberApi } from "@/api/DocumentAutoGeneratedNumberApi";
import { EDocumentProductDataType } from "@/formTypes/EDocumentProductDataType";
import { InvoiceTemplateFormDataKeys } from "@/generated/InvoiceTemplateFormDataKeys";
import { showAlert } from "@utils/Alert";
import { parseStoredOtherCharges, resolveOtherCharges, serializeStoredOtherCharges } from "@utils/DocumentOtherCharges";

type InvoiceTemplateFormProps = {
  id?: string;
  copyData?: boolean
  formId: string,
  saveRedirectUrl: string
}

const InvoiceTemplateForm = (props: InvoiceTemplateFormProps) => {
  const dispatch = useAppDispatch()
  const { copyData = false, id: preLoadedInvoiceTemplateId, saveRedirectUrl } = props
  const formRef = useRef<DynamicFormRef>(null);
  const [defaultData, setDefaultData] = useState<InvoiceTemplateDataType>({});
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const suscriptionData = useAppSelector((state) => state.suscription.suscription);
  const documentCurrency = useAppSelector((state) => state.currency);
  const [formConfig, setFormConfig] = useState<any>();

  useEffect(() => {
    setIsLoading(true)
    loadFormConfig()
    loadDefaultData()
  }, [suscriptionData])

  const loadFormConfig = () => {
    setFormConfig(InvoiceTemplateFormConfig)
  }

  const onInputUpdate = (name: keyof InvoiceTemplateFormData, value: any, event?: React.ChangeEvent<any>) => {

  }

  const onInputKeyDown = (name: keyof InvoiceTemplateFormData | "client_code", event?: React.KeyboardEvent<any>) => {

  }

  const preLoadInvoiceTemplateData = async () => {
    if (preLoadedInvoiceTemplateId) {
      let invoice = await InvoiceTemplateApi.get(preLoadedInvoiceTemplateId, {
        selectionSet: [
          ...InvoiceTemplateFormDataKeys.filter(k => !InvoiceTemplateRelatedAttributes.includes(k)),
          "currency.*",
          "invoiceTemplateProducts.*",
          "invoiceTemplateProducts.product.*"
        ]
      })

      dispatch(setDocumentDiscount(Number(invoice.porcenta_descuento || 0)))
      dispatch(setDocumentOtherCharges(parseStoredOtherCharges(invoice.otros_cargos)))

      let formData = omitProperties(invoice, ["createdAt", "updatedAt"], true);
      if (copyData) {
        formData = omitProperties(invoice, ["id", "numero"]);
      }
      formData = omitProperties(invoice, ["createdAt", "updatedAt"], true);
      formRef.current?.resetFormValue({
        ...formData
      })

      if (invoice.invoiceTemplateProducts) {
        const invoiceProducts = invoice.invoiceTemplateProducts.map(ip => {
          let product = ip;

          if (copyData) {
            product = omitProperties(product, ["id", "invoiceTemplateId"]);
          } else {
            product.preSavedId = product.id
          }
          return omitProperties(product, ["invoiceTemplateId", "createdAt", "updatedAt"], true);
        })
        dispatch(loadProducts(invoiceProducts as EDocumentProductDataType[]))
      }
      if (invoice.currency && invoice.currencyId) {
        CurrencyIdPubSub.publish({ currencyId: invoice.currencyId })
      }
    }
  }

  const loadDefaultData = async (resetLoad = false) => {
    try {
      loadDefaultPrice()
      if (!resetLoad) {
        await preLoadInvoiceTemplateData()
      } else {
        let defaultData: InvoiceTemplateDataType = {}

        const defaultCurrency = await CurrencyApi.getDefaultCurrency()
        if (defaultCurrency.id) {
          dispatch(setCurrency(removeFunctionFromObject(defaultCurrency)))
          defaultData["currencyId"] = defaultCurrency.id
          if (defaultCurrency.exchange_rate) {
            defaultData["exchange_rate"] = defaultCurrency.exchange_rate
          }
          CurrencyIdPubSub.publish({ currencyId: defaultCurrency.id })
        }

        formRef.current?.setFormValue("multiple", defaultData, "multipleData")
        setDefaultData(defaultData)
        if (resetLoad) {
          formRef.current?.resetFormValue(defaultData)
        }
      }
    } finally {
      setIsLoading(false)
    }
  }

  const loadDefaultPrice = async () => {
    const defaultPrice = await PriceApi.getDefaultPrice()

    if (typeof defaultPrice === 'number') {
      dispatch(setPriceCode(defaultPrice))
    } else {
      let priceData = removeFunctionFromObject(defaultPrice)
      dispatch(setPrice(priceData))
    }
  }

  const createInvoiceTemplate = async (formData: InvoiceTemplateDataType): Promise<InvoiceTemplateDataType> => {
    const eDocumentState = flushDocumentProductEdits();
    const documentSummaryState = selectEDocumentSummary(eDocumentState);
    let invoiceData: InvoiceTemplateDataType = {}
    const { documentProducts, documentDiscountType, isDocumentExempted } = eDocumentState

    if (documentProducts?.length == 0) {
      throw Error("Por favor agregue algun producto a la  plantilla de factura")
    }

    let generatedNumber = await generateNextNumberByModel("InvoiceTemplate");
    if (!generatedNumber?.number) {
      throw Error("No fue posible generar el identificador de la factura")
    }

    invoiceData = {
      ...formData,
      subtotal: documentSummaryState.subtotal,
      descuento: documentDiscountType == ON_INVOICE_DISCOUNT_TYPE_CODE ? documentSummaryState.descuento : 0,
      impuestos: documentSummaryState.impuestos,
      total: documentSummaryState.total,
      currencyId: documentCurrency.currencyApiId,
      exenta: isDocumentExempted,
      numero: generatedNumber?.number,
      porcenta_descuento: eDocumentState.documentDiscount,
      otros_cargos: serializeStoredOtherCharges(
        resolveOtherCharges(eDocumentState.documentOtherCharges, selectEDocumentVentaNeta(eDocumentState)),
      )
    }

    if (documentProducts) {
      const createdInvoiceTemplate = await InvoiceTemplateApi.createInvoiceTemplateAndProducts(invoiceData, documentProducts as InvoiceTemplateProductDataType[])
      await DocumentAutoGeneratedNumberApi.save(generatedNumber)
      clearForm()
      showAlert("success", "La plantilla de Facturacionha sido creada existosamente")
      return createdInvoiceTemplate;
    }

    return {} as InvoiceTemplateDataType;
  }

  const updateInvoiceTemplate = async (templateId: string, formData: InvoiceTemplateDataType): Promise<InvoiceTemplateDataType> => {
    const eDocumentState = flushDocumentProductEdits();
    const documentSummaryState = selectEDocumentSummary(eDocumentState);
    let invoiceData: InvoiceTemplateDataType = {}
    const { documentProducts, documentDiscountType, isDocumentExempted } = eDocumentState

    if (documentProducts?.length == 0) {
      throw Error("Por favor agregue algun producto a la  plantilla de factura")
    }

    invoiceData = {
      ...formData,
      subtotal: documentSummaryState.subtotal,
      descuento: documentDiscountType == ON_INVOICE_DISCOUNT_TYPE_CODE ? documentSummaryState.descuento : 0,
      impuestos: documentSummaryState.impuestos,
      total: documentSummaryState.total,
      currencyId: documentCurrency.currencyApiId,
      exenta: isDocumentExempted,
      porcenta_descuento: eDocumentState.documentDiscount,
      otros_cargos: serializeStoredOtherCharges(
        resolveOtherCharges(eDocumentState.documentOtherCharges, selectEDocumentVentaNeta(eDocumentState)),
      )
    }

    if (documentProducts) {
      const updatedInvoiceTemplate = await InvoiceTemplateApi.updateInvoiceTemplateAndProducts(invoiceData, documentProducts as InvoiceTemplateProductDataType[])
      clearForm()
      showAlert("success", "La plantilla de Facturacion ha sido actualizada existosamente")
      return updatedInvoiceTemplate;
    }

    return {} as InvoiceTemplateDataType;
  }

  const clearForm = async () => {
    dispatch(clearDocument())
    loadDefaultData(true)
  }

  const getCreateConfirmation = (): ConfirmationConfig | undefined => {
    const { ask_confirm = false } = suscriptionData || {}
    if (ask_confirm) {
      return {
        message: "Esta seguro que desea crear la plantilla de Facturacion"
      }
    }
  }

  return (
    <>
      {formConfig?.sections && (<GenericForm<InvoiceTemplateFormData, InvoiceTemplateDataType>
        ref={formRef}
        formId={props.formId}
        confirmationConfig={getCreateConfirmation()}
        defaultData={defaultData}
        modelRelativeObjects={InvoiceTemplateRelatedAttributes}
        onInputChange={onInputUpdate}
        onInputKeyDown={onInputKeyDown}
        formConfig={formConfig}
        saveRedirectUrl={saveRedirectUrl}
        copyData={copyData}
        getById={(...arg) => InvoiceTemplateApi.get(...arg)}
        createFn={(...arg) => createInvoiceTemplate(...arg)}
        updateFn={(id, ...rest) => updateInvoiceTemplate(id, ...rest)}
      >
        <Row>
          <Col>
            <h3>
              Productos
            </h3>
          </Col>
          <Col xs="1">
            <PriceSelector />
          </Col>
        </Row>
        <QuickAddProductForm />
        <EdocumentProductList showOtherCharges={true} />
      </GenericForm >
      )}
      <LoadingScreen isLoading={isLoading} />
    </>

  )
}

export default InvoiceTemplateForm;